import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from 'uinix-ui';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Entity } from '~/types/Entity';

export const ConversationHistory = () => {
  const dispatch = useDispatch();
  const styles = useStyles();
  const intl = useIntl();
  const formatDate = useFormatDate();
  const currentChatbot = useSelector(
    slices.chatbot.selectors.selectCurrentChatbot,
  );
  const [interruptAiMessage, { isLoading: isInterruptingAiMessage }] =
    api.endpoints.interruptAiMessage.useMutation();

  const currentConversationId = useSelector(
    slices.chatbot.selectors.selectCurrentConversationId,
  );

  const { data: conversations, isLoading: isLoadingConversations } =
    api.endpoints.getConversations.useQuery(
      currentChatbot as Entity<
        'document_handler' | 'document_version' | 'all_documents'
      >,
    );

  const handleConversationClick = (conversationId: string) => {
    interruptAiMessage(currentConversationId ?? '')
      .unwrap()
      .finally(() => {
        dispatch(
          slices.chatbot.actions.setCurrentConversationId(conversationId),
        );
      });
  };

  const isEmpty = conversations?.items.length === 0;

  return (
    <Layout w="100%" h="100%">
      <ContentContainer
        loadingContent={{ isLoading: isLoadingConversations }}
        placeholderContent={
          isEmpty
            ? {
                message: intl.formatMessage({
                  id: 'ConversationHistory.noConversations',
                  defaultMessage: 'No conversations found',
                  description: 'Message when no conversations are found',
                }),
              }
            : undefined
        }
      >
        <Layout direction="column" w="100%">
          {conversations?.items.map((conversation) => {
            const isActive = conversation.id === currentConversationId;
            return (
              <Layout
                key={`conversation_${conversation.id}`}
                px={4}
                py={2}
                borderRadius="m"
                onClick={
                  isActive
                    ? undefined
                    : () => handleConversationClick(conversation.id)
                }
                styles={styles.colors.status}
                styleProps={{
                  status: isActive ? 'active' : undefined,
                }}
              >
                <Icon
                  icon="chatbot"
                  color={isActive ? 'status.active' : 'status.inactive'}
                />
                <Layout ml={4}>
                  <TruncateText variant="body-medium">
                    {formatDate(conversation.datetimeCreated, 'short_datetime')}
                  </TruncateText>
                </Layout>
                {isActive && isInterruptingAiMessage && (
                  <Layout ml={4}>
                    <LoadingSpinner />
                  </Layout>
                )}
              </Layout>
            );
          })}
        </Layout>
      </ContentContainer>
    </Layout>
  );
};
